import React from 'react';
import { InformationCircleIcon } from '@heroicons/react/outline';

/**
 * InfoField
 * ---------
 * Purpose:
 *   Renders informational/instructional text within a form without requiring
 *   user input. Used to provide context, guidance, or important notices.
 *
 * Schema structure expected:
 *   {
 *     "questionId": "some_info",
 *     "type": "info",
 *     "label": "This is the informational text to display. Supports newlines.\n\nSecond paragraph here.",
 *     "required": false
 *   }
 *
 * Behaviour:
 *   - Displays the label text as formatted paragraphs
 *   - Supports newline characters (\n) for paragraph breaks
 *   - Optionally shows an info icon for visual emphasis
 *   - No input is collected; this is purely informational
 *
 * Props:
 *   - question: The info question node from schema
 *   - showIcon: Whether to show the info icon (default: true)
 */
export default function InfoField({ question, showIcon = true }) {
  const label = question?.label || '';
  
  // Split by newlines to create paragraphs
  const paragraphs = label.split('\n').filter(p => p.trim());

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
      <div className="flex gap-3">
        {showIcon && (
          <InformationCircleIcon className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
        )}
        <div className="flex-1 space-y-3">
          {paragraphs.map((paragraph, idx) => (
            <p key={idx} className="text-sm text-blue-800 leading-relaxed">
              {paragraph.trim()}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}
